<template>
  <div class="cart-container">
    <div class="header">
      <div class="heading-container">
        <div class="heading">{{ $translate('BOX_HEADING_SHOPPING_CART') }}</div>
        <div role="status" class="sub-heading">
          {{ $translate('YOU_HAVE_X_ITEMS_IN_YOUR_SHOPPING_CART', {
            productCount: shoppingCartStore.cartCount
          }) }}
        </div>
      </div>

      <button 
        @click="closeCart"
        :tabindex="cartTabindex"
        class="close"
        :aria-label="`${$translate('TEXT_CLOSE')} ${$translate('BOX_HEADING_SHOPPING_CART')}`"
        ref="closeCartButton"
        @keydown="handleCheckoutKeydown"
        data-close-cart-button
      >
        <CloseIcon />
      </button> 
    </div>

    <div class="content">
      <ShoppingCartItem
        v-for="product in shoppingCartStore.products"
        :key="`${Date.now()}_${product.id}`"
        :product="product"
        :cartTabindex="cartTabindex"
        :showStockStatus="showStockStatus"
        :currency="props.currency"
      />
    </div>

    <div class="footer">
      <div v-if="showLeftToFreeShipping" class="shipping-info-container">
        <div>
          <span>Frakt</span>
        </div>
        
        <LeftToFreeShipping class="left-to-free-shipping-component" />
      </div>
      
      <div v-if="shoppingCartStore.products?.length">
        <div class="total-container">
          <span class="text-sum">{{ $translate('BOX_SHOPPING_CART_SUB_TOTAL_BOTTOM') }}</span>
          <span class="total">{{ formattedTotal }}</span>
        </div>
  
        <a 
          href="/checkout.php"
          class="button primary expanded"
          :aria-label="$translate('BUTTON_SHOPPING_CART')"
          :tabindex="cartTabindex"
          ref="goToCheckoutLink"
          @keydown="handleCheckoutKeydown"
        >
          {{ $translate('BUTTON_SHOPPING_CART') }}
        </a>
      </div>
    </div>

  </div>
</template>

<script setup>
import CloseIcon from '@/components/icons/CloseIcon.vue';
import ShoppingCartItem from '@/components/ShoppingCartItem.vue';
import LeftToFreeShipping from '@/components/LeftToFreeShipping.vue';
import { useShoppingCartStore } from '@/stores/shopping-cart.js';
import { useCurrencyFormatter } from '@/composables/use-currency-formatter.js';
import { computed, ref } from 'vue';

const props = defineProps({
  showStockStatus: {
    required: false,
    type: Boolean,
    default: false
  },
  currency: {
    type: String,
    required: false,
    default: 'SEK'
  }
});

const showLeftToFreeShipping = window.vendre_config.general.show_left_to_free_shipping;
const closeCartButton = ref();
const goToCheckoutLink = ref();

const shoppingCartStore = useShoppingCartStore();

const cartTabindex = computed(() => {
  return shoppingCartStore.showCart ? 0 : -1;
});

const { formatPrice } = useCurrencyFormatter(props.currency);

const formattedTotal = computed(() => {
  return formatPrice(shoppingCartStore.total);
});


document.addEventListener('exitOffCanvas', () => {
  shoppingCartStore.showCart = false;
});

const handleCheckoutKeydown = (event) => {
  if (event.key === 'Tab' && !event.shiftKey && event.target === goToCheckoutLink.value) {
    event.preventDefault();
    closeCartButton.value.focus();
  } else if (event.key === 'Tab' && event.shiftKey && event.target === closeCartButton.value) {
    event.preventDefault();
    goToCheckoutLink.value.focus();
  }
};

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeCart();
  }
});

const closeCart = () => {
  document.querySelector('[data-exit-off-canvas]').click();
  shoppingCartStore.showCart = false;
  Accessibility.hideVoiceOver('[data-slide-in-object="data-cart"]');
}
</script>